/**
 * Secrets Collective — bundle builder.
 *
 * Each set is an independent variant selection of the same product. On submit
 * all sets go to the cart in one request as separate line items, which is what
 * a Buy X Get Y automatic discount needs in order to zero the cheapest.
 */

import { addItems } from './sc-bundle.js';

class SCBundle extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.setCount = Number(this.dataset.setCount) || 3;
    this.freeCount = Number(this.dataset.freeCount) || 0;

    // selections[setIndex][optionIndex] = value
    this.selections = {};

    this.cta = this.querySelector('[ref="cta"]');
    this.ctaText = this.querySelector('[ref="ctaText"]');
    this.msg = this.querySelector('[ref="msg"]');
    this.ctaLabel = this.ctaText?.textContent?.trim() || 'Add to bag';

    this.moneyFormat = this.dataset.moneyFormat || '';
    this.savePrefix = this.dataset.savePrefix || 'Save';
    this.pctSuffix = this.dataset.pctSuffix || 'off';
    this.hideDecimals = this.dataset.hideDecimals !== 'false';

    this.#seed();
    this.#bind();
    this.#update();

    this.cta?.addEventListener('click', () => this.#submit());
  }

  #readVariants() {
    const el = this.querySelector('[ref="variantData"]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch {
      return [];
    }
  }

  #seed() {
    this.querySelectorAll('[data-set][data-option-index][aria-pressed="true"]').forEach((btn) => {
      const s = btn.dataset.set;
      this.selections[s] = this.selections[s] || {};
      this.selections[s][btn.dataset.optionIndex] = btn.dataset.value;
    });
  }

  #bind() {
    this.querySelectorAll('[data-set][data-option-index]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const { set, optionIndex } = btn.dataset;
        this.querySelectorAll(
          `[data-set="${set}"][data-option-index="${optionIndex}"]`
        ).forEach((o) => o.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        this.selections[set] = this.selections[set] || {};
        this.selections[set][optionIndex] = btn.dataset.value;
        this.#update();
      });
    });
  }

  #variantFor(setIndex) {
    const picks = this.selections[String(setIndex)];
    if (!picks) return null;
    const chosen = Object.keys(picks)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => picks[k]);
    return this.variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
  }

  #update() {
    let total = 0;
    let complete = true;

    for (let i = 1; i <= this.setCount; i++) {
      const variant = this.#variantFor(i);
      const summary = this.querySelector(`[ref="summary${i}"]`);
      const picks = this.selections[String(i)] || {};

      if (summary) {
        summary.textContent = Object.keys(picks)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => picks[k])
          .join(' · ');
      }

      if (!variant || !variant.available) {
        complete = false;
      } else {
        total += variant.price;
      }

      // Grey out combinations that don't exist for this set.
      this.querySelectorAll(`[data-set="${i}"][data-option-index]`).forEach((btn) => {
        const trial = { ...picks, [btn.dataset.optionIndex]: btn.dataset.value };
        const chosen = Object.keys(trial)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => trial[k]);
        const exists = this.variants.some(
          (v) => v.available && chosen.every((val, idx) => v.options[idx] === val)
        );
        btn.classList.toggle('is-unavailable', !exists);
      });
    }

    this.#renderPrices(total, complete);

    if (this.cta) this.cta.disabled = !complete;
    if (this.ctaText) {
      this.ctaText.textContent = complete ? this.ctaLabel : 'Choose every option';
    }
  }

  /**
   * The free sets are the cheapest N — matching how Shopify's Buy X Get Y
   * discount actually resolves — so the preview never overstates the saving.
   */
  #renderPrices(total, complete) {
    const was = this.querySelector('[ref="wasPrice"]');
    const now = this.querySelector('[ref="nowPrice"]');
    const savePill = this.querySelector('[ref="savePill"]');
    const pctPill = this.querySelector('[ref="pctPill"]');
    if (!was || !now) return;

    if (!complete) {
      [was, now, savePill, pctPill].forEach((el) => el && (el.textContent = ''));
      return;
    }

    const prices = [];
    for (let i = 1; i <= this.setCount; i++) {
      const v = this.#variantFor(i);
      if (v) prices.push(v.price);
    }
    prices.sort((a, b) => a - b);
    const freeValue = prices.slice(0, this.freeCount).reduce((s, p) => s + p, 0);
    const payable = total - freeValue;

    const fmt = (c) => money(c, this.moneyFormat, this.hideDecimals);
    was.textContent = this.freeCount > 0 ? fmt(total) : '';
    now.textContent = fmt(payable);
    if (savePill) {
      savePill.textContent = freeValue > 0 ? `${this.savePrefix} ${fmt(freeValue)}` : '';
    }
    if (pctPill) {
      const pct = total > 0 ? Math.round((freeValue / total) * 100) : 0;
      pctPill.textContent = pct > 0 ? `${pct}% ${this.pctSuffix}` : '';
    }
  }

  async #submit() {
    if (!this.cta || this.cta.disabled) return;

    const items = [];
    for (let i = 1; i <= this.setCount; i++) {
      const v = this.#variantFor(i);
      if (!v) return;
      items.push({ id: v.id, quantity: 1 });
    }

    this.cta.disabled = true;
    const original = this.ctaText.textContent;
    this.ctaText.textContent = 'Adding…';
    this.msg?.classList.remove('is-error');

    const result = await addItems(this, items);

    if (result.ok) {
      this.ctaText.textContent = 'Added';
      if (this.msg) this.msg.textContent = this.dataset.successMessage || '';
      setTimeout(() => {
        this.ctaText.textContent = original;
        this.cta.disabled = false;
      }, 1600);
    } else {
      this.ctaText.textContent = original;
      this.cta.disabled = false;
      if (this.msg) {
        this.msg.textContent = result.message || 'Could not add to cart';
        this.msg.classList.add('is-error');
      }
    }
  }
}

/**
 * Format cents using the shop's own money format so the currency matches the
 * rest of the store (AUD here) rather than assuming a bare dollar sign.
 */
function money(cents, format, hideDecimals) {
  const amount = cents / 100;
  // Whole amounts read cleaner as "$298" than "$298.00" in badges and headings.
  const plain = hideDecimals && Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  const withCommas = plain.replace(/\B(?=(\d{3})+(?!\d)(?!\.))/g, ',');

  if (typeof format === 'string' && format.includes('{{')) {
    return format.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_m, token) => {
      switch (token) {
        case 'amount_no_decimals':
          return Math.round(amount).toLocaleString('en');
        case 'amount_with_comma_separator':
          return plain.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d),)/g, '.');
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(amount).toLocaleString('de');
        default:
          return withCommas;
      }
    });
  }
  return `$${withCommas}`;
}

if (!customElements.get('sc-bundle')) {
  customElements.define('sc-bundle', SCBundle);
}
